import {
  Search,
  Printer,
  Trash2,
  ChevronDown,
  FileSearch,
  PlusCircle,
} from "lucide-react";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { useGetTransactionsQuery } from "../../redux/services/Wallet";
import TransactionHistoryTableFull from "../../components/Table/TransactionHistoryTableFull";
import FleetrifyBlockSkeleton from "../../components/Loading/BlockSkeleton";

type SortByAmount = "highest" | "lowest" | "";
type StatusFilter = "completed" | "pending" | "failed" | "";

const amountOptions: { label: string; value: SortByAmount }[] = [
  { label: "All Amounts", value: "" },
  { label: "Lowest First", value: "lowest" },
  { label: "Highest First", value: "highest" },
];

const statusOptions: { label: string; value: StatusFilter }[] = [
  { label: "All Status", value: "" },
  { label: "Completed", value: "completed" },
  { label: "Pending", value: "pending" },
  { label: "Failed", value: "failed" },
];

const TransactionHistory = () => {
  const [isAmountOpen, setIsAmountOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(amountOptions[0]);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, isError } = useGetTransactionsQuery({
    page: currentPage,
    per_page: 10,
    search: debouncedSearch || undefined,
    status: selectedStatus.value || undefined,
    sort_by_amount: selectedAmount.value || undefined,
  });

  const transactions = data?.data?.data ?? [];
  const hasTransactions = transactions.length > 0;
  const isFiltered = !!(debouncedSearch || selectedStatus.value || selectedAmount.value);

  const handleAmountSelect = (option: typeof amountOptions[number]) => {
    setSelectedAmount(option);
    setIsAmountOpen(false);
    setCurrentPage(1);
  };

  const handleStatusSelect = (option: typeof statusOptions[number]) => {
    setSelectedStatus(option);
    setIsStatusOpen(false);
    setCurrentPage(1);
  };

  // ✅ Loading State
  if (isLoading) {
    return (
      <div className="mt-10">
        <FleetrifyBlockSkeleton lines={8} className="h-[400px]" />
      </div>
    );
  }

  // ✅ Error State
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-xl p-10 mt-10 shadow-sm transition-all duration-300">
        <div className="bg-red-100 p-4 rounded-full mb-4">
          <FileSearch className="h-12 w-12 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-red-600 mb-1">
          Failed to Load Transactions
        </h3>
        <p className="text-sm text-red-500 max-w-sm text-center mb-4">
          Something went wrong while fetching your transactions. Please try
          again later.
        </p>
      </div>
    );
  }

  // ✅ Empty State (no transactions have ever existed — no filters applied)
  if (!hasTransactions && !isFiltered) {
    return (
      <div className="flex flex-col items-center justify-center bg-gray-50 border border-gray-200 rounded-xl p-10 mt-10 shadow-sm transition-all duration-300">
        <div className="bg-gray-100 p-4 rounded-full mb-4">
          <FileSearch className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-1">
          No Transactions Found
        </h3>
        <p className="text-sm text-gray-500 max-w-sm text-center mb-4">
          You don’t have any wallet transactions yet. Once you withdraw funds or make a
          payment, your transaction history will appear here.
        </p>
        {/* <button
          onClick={() => console.log("Navigate to fund wallet")}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-fleetblue hover:bg-fleetblue/90 rounded-lg shadow-sm transition-all"
        >
          <PlusCircle className="h-4 w-4" />
          Fund Wallet
        </button> */}
      </div>
    );
  }

  // ✅ Main Table View
  return (
    <div>
      <section className="border border-gray-300 rounded-lg mt-10">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-[#00032A] mb-4">
            Transaction activity history
          </h3>

          {/* ✅ FILTER + ACTION BAR */}
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search Input */}
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by reference or description"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-[30px] text-sm outline-none focus:ring-1 focus:ring-fleetblue"
                />
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-fleetblack font-semibold">
                  Filter By:
                </span>

                {/* Amount Filter Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setIsAmountOpen(!isAmountOpen);
                      setIsStatusOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-[30px] text-sm hover:bg-gray-50"
                  >
                    {selectedAmount.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isAmountOpen ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  {isAmountOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                      {amountOptions.map((option) => (
                        <button
                          key={option.label}
                          onClick={() => handleAmountSelect(option)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Status Filter Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setIsStatusOpen(!isStatusOpen);
                      setIsAmountOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-[30px] text-sm hover:bg-gray-50"
                  >
                    {selectedStatus.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isStatusOpen ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  {isStatusOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                      {statusOptions.map((option) => (
                        <button
                          key={option.label}
                          onClick={() => handleStatusSelect(option)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Icons */}
            <div className="flex items-center gap-2 ml-2">
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <Printer className="w-5 h-5 text-gray-600" />
              </button>
              {/* <button className="p-2 hover:bg-gray-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-gray-600" />
              </button> */}
            </div>
          </div>
        </div>

        {/* ✅ TABLE */}
        <div>
          {!hasTransactions && isFiltered ? (
            <div className="flex flex-col items-center justify-center p-10">
              <div className="bg-gray-100 p-4 rounded-full mb-4">
                <FileSearch className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">
                No matching transactions
              </h3>
              <p className="text-sm text-gray-500 max-w-sm text-center mb-4">
                No transactions match your current search or filters. Try adjusting them.
              </p>
              <button
                onClick={() => {
                  setSearch("");
                  setSelectedAmount(amountOptions[0]);
                  setSelectedStatus(statusOptions[0]);
                  setCurrentPage(1);
                }}
                className="text-sm font-medium text-fleetblue hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <TransactionHistoryTableFull
              transactions={transactions}
              isLoading={isFetching}
              total={data?.data?.total ?? 0}
              currentPage={data?.data?.current_page ?? 1}
              lastPage={data?.data?.last_page ?? 1}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default TransactionHistory;
